import { Box, Button, FloatingIndicator, Group, Paper, Stack, Tabs, Text, TextInput } from '@mantine/core'
import { IconLink, IconPencil, IconUpload } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { allUploadAccept, FileUpload } from '@/components/FileUpload'
import { LiveModuleBanner } from '@/components/LiveModuleBanner'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import { AddStepDataContentModal } from '@/screens/ContentModuleFormScreen/AddStepDataContentModal'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import { useUploadModalState } from '@/screens/ContentModuleFormScreen/useUploadModalState'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { castStepToZodForm } from '@/utils/contentModuleForm'

type ContentTabValue = 'upload' | 'website' | 'manual'

type UploadStepModalProps = CMFormSection & {
  /** Opened for the single "Primary Media File" slot of a media-mode module. */
  isPrimary?: boolean
}

export const UploadStepModal = observer(function UploadStepModal(props: UploadStepModalProps) {
  const { uiStore } = useGlobalState()
  const [contentTab, setContentTab] = useState<ContentTabValue>('upload')
  const tabInitializedRef = useRef(false)
  const [tabsRootRef, setTabsRootRef] = useState<HTMLDivElement | null>(null)
  const [tabControlsRefs, setTabControlsRefs] = useState<Record<string, HTMLButtonElement | null>>({})

  const {
    stepResources,
    isExtracting,
    websiteUrl,
    setWebsiteUrl,
    websiteUrlError,
    handleWebsiteUrlBlur,
    handleExtractFromWebsite,
    pendingFileName,
    setPendingFileName,
    uploadPhase,
    uploadProgress,
    processProgressPct,
    cancelContentUpload,
    processDroppedFiles,
    isStepCreating,
    cancelStepCreation,
    hasContent,
    contentLoading,
    uploadedFileResource,
  } = useUploadModalState(props)

  // Manual text isn't a valid source for a media module's primary file.
  const hideManualTab = props.isMediaMode && !!props.isPrimary

  const getDefaultContentTab = useCallback(() => {
    const sourceFile = stepResources?.find(r => r.isSourceFile)
    if (!sourceFile) return 'upload'
    if (sourceFile.type === 'url' || sourceFile.type === 'youtube') return 'website' as ContentTabValue
    if (sourceFile.type === 'text' && !hideManualTab) return 'manual' as ContentTabValue
    return 'upload' as ContentTabValue
  }, [stepResources, hideManualTab])

  const openDataContentModal = useCallback(() => {
    props.openModal?.({
      title: `Edit File Content Manually`,
      subtitle: 'Copy and paste or enter your content manually.',
      Component: AddStepDataContentModal,
      ComponentProps: {
        initialContent: props.stepContentQuery.data?.content ?? '',
      },
      ModalProps: {
        size: '95vw',
      },
    })
  }, [props.stepContentQuery.data, props.openModal])

  const handleDone = useCallback(() => {
    props.closeModal(() => {
      props.stepForm.setValues(castStepToZodForm())
      uiStore.setPendingStepDeletion(false)
      props.signalStepSaved?.()
    })
  }, [props.closeModal, props.stepForm, uiStore, props.signalStepSaved])

  const handleCancel = useCallback(() => {
    props.closeModal(() => {
      const state = uiStore.pendingStepDeletion
      props.stepForm.setValues(castStepToZodForm())
      props.faqActions.resetStepFAQState()
      if (state) {
        props.stepDeleteMutation?.mutate({
          step_id: props.stepId!,
          task_id: props.taskId!,
          showToast: false,
        })
      } else if (isStepCreating) {
        cancelStepCreation()
      } else {
        cancelContentUpload()
      }
      uiStore.setPendingStepDeletion(false)
    })
  }, [
    props.closeModal,
    props.stepForm,
    props.faqActions,
    props.stepDeleteMutation,
    props.stepId,
    props.taskId,
    uiStore,
    isStepCreating,
    cancelStepCreation,
    cancelContentUpload,
  ])

  const setTabControlRef = useMemo(() => {
    const cache: Record<string, (node: HTMLButtonElement | null) => void> = {}
    return (val: string) => {
      if (!cache[val]) {
        cache[val] = (node: HTMLButtonElement | null) => {
          setTabControlsRefs(prev => (prev[val] === node ? prev : { ...prev, [val]: node }))
        }
      }
      return cache[val]
    }
  }, [])

  useEffect(() => {
    if (!tabInitializedRef.current && stepResources !== undefined) {
      if (props.stepId) {
        setContentTab(getDefaultContentTab())
      }
      tabInitializedRef.current = true
    }
  }, [stepResources])

  // Pre-fill website URL when editing an existing step created from a URL
  useEffect(() => {
    if (props.stepId && stepResources) {
      const sourceResource = stepResources.find(r => (r.type === 'url' || r.type === 'youtube') && r.isSourceFile)
      const sourceUrl = sourceResource?.data || sourceResource?.sourceUrl || ''
      setWebsiteUrl(prev => (prev !== sourceUrl ? sourceUrl : prev))
    } else {
      setWebsiteUrl('')
    }
  }, [props.stepId, stepResources])

  return (
    <Stack bg={theme.colors.gray[0]} gap="xl" pb="xl">
      {props.isLive && <LiveModuleBanner />}
      <Paper shadow="sm" p="md" radius="md">
        <Text size="lg" fw={600} mb={hasContent ? 'xs' : 'md'}>
          Add Content
        </Text>

        {hasContent && (
          <Text size="sm" c="dimmed" mb="md">
            Content has been uploaded. Click Done to continue.
          </Text>
        )}

        <Tabs variant="none" value={contentTab} onChange={v => v && setContentTab(v as ContentTabValue)}>
          <Tabs.List ref={setTabsRootRef} className={classes.contentTabsList}>
            <Tabs.Tab
              value="upload"
              ref={setTabControlRef('upload')}
              classNames={classes.contentTabClassNames}
              leftSection={<IconUpload size={18} />}
              disabled={contentLoading || hasContent}>
              Upload File
            </Tabs.Tab>
            <Tabs.Tab
              value="website"
              ref={setTabControlRef('website')}
              classNames={classes.contentTabClassNames}
              leftSection={<IconLink size={18} />}
              disabled={contentLoading || hasContent}>
              Provide URL
            </Tabs.Tab>
            {!hideManualTab && (
              <Tabs.Tab
                value="manual"
                ref={setTabControlRef('manual')}
                classNames={classes.contentTabClassNames}
                leftSection={<IconPencil size={18} />}
                disabled={contentLoading || hasContent}>
                Add Manually
              </Tabs.Tab>
            )}

            <FloatingIndicator
              target={contentTab ? tabControlsRefs[contentTab] : null}
              parent={tabsRootRef}
              className={classes.contentTabIndicator}
            />
          </Tabs.List>

          <Tabs.Panel value="upload">
            <Box className={classes.tabPanelWrapper}>
              <Stack gap="md">
                <FileUpload
                  activateOnClick={false}
                  loading={contentLoading}
                  disabled={hasContent}
                  uploadPhase={uploadPhase}
                  uploadProgress={uploadProgress}
                  processProgress={processProgressPct}
                  onDrop={processDroppedFiles}
                  error={props.stepForm.getInputProps('content.file').error}
                  titles={{
                    idle: 'Drag file here to upload',
                  }}
                  EmptyStateProps={{
                    TitleProps: {
                      size: 'lg',
                      fw: 700,
                    },
                    gap: 'xs',
                  }}
                  accept={allUploadAccept}
                  maxFiles={1}
                  primaryActionLabel="Select File"
                  actionButtonProps={{
                    variant: 'outline',
                    color: 'gray',
                    size: 'sm',
                  }}
                  hints={{
                    idle: 'Valid file types: png, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, md, pdf, pptx and docx',
                  }}
                />

                {uploadedFileResource && (
                  <UploadedFilePreview
                    fileName={pendingFileName || uploadedFileResource.fileName || 'Uploaded file'}
                    type={uploadedFileResource.type}
                    resourceKey={uploadedFileResource.resourceKey}
                    resourceData={uploadedFileResource.data}
                    onFileNameChange={setPendingFileName}
                  />
                )}
              </Stack>
            </Box>
          </Tabs.Panel>

          <Tabs.Panel value="website">
            <Box className={classes.tabPanelWrapper}>
              <Stack gap="md">
                <Stack gap="xs">
                  <Text size="sm">URL</Text>
                  <Group gap="sm" align="stretch">
                    <TextInput
                      placeholder="https://example.com/article"
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.currentTarget.value)}
                      onBlur={handleWebsiteUrlBlur}
                      size="md"
                      disabled={hasContent || isExtracting || uploadPhase !== 'idle'}
                      leftSection={<IconLink size={18} />}
                      error={websiteUrlError}
                      style={{ flex: 1 }}
                      radius="md"
                      variant="filled"
                    />
                    <Button
                      size="md"
                      radius="md"
                      variant="light"
                      onClick={handleExtractFromWebsite}
                      loading={isExtracting}
                      disabled={hasContent || !websiteUrl.trim() || uploadPhase !== 'idle'}>
                      Add
                    </Button>
                  </Group>
                </Stack>
                <Paper shadow="none" p="md" radius="sm" bg="gray.0">
                  <Text size="sm" c="dimmed">
                    Enter a URL for content you want to add. Supports most article pages, blog posts, documentation, and
                    YouTube videos.
                  </Text>
                </Paper>
              </Stack>
            </Box>
          </Tabs.Panel>

          {!hideManualTab && (
            <Tabs.Panel value="manual">
              <Box className={classes.tabPanelWrapper}>
                <Stack gap="md" align="center" justify="center" style={{ minHeight: 150 }}>
                  <Button variant="light" onClick={openDataContentModal} disabled={contentLoading || hasContent}>
                    Add Content
                  </Button>
                </Stack>
              </Box>
            </Tabs.Panel>
          )}
        </Tabs>

        {contentLoading && (
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} mt="sm">
            <Text c="dimmed" className={classes.inputDescription}>
              {uploadPhase === 'uploading' && 'Uploading file...'}
              {uploadPhase === 'processing' &&
                'Processing content can take some time, depending on your bandwidth. Files over 1 gigabyte can potentially take hours.'}
              {uploadPhase === 'idle' && 'Loading content...'}
            </Text>
          </Box>
        )}
      </Paper>

      <Group gap="lg" justify="flex-end">
        <Button variant="light" color="gray" radius="md" onClick={handleCancel}>
          Cancel
        </Button>

        <Button onClick={handleDone} disabled={!hasContent || uploadPhase !== 'idle' || isStepCreating} radius="md">
          Done
        </Button>
      </Group>
    </Stack>
  )
})
